import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useEditor, EditorContent, ReactRenderer } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableHeader } from '@tiptap/extension-table-header';
import { TableCell } from '@tiptap/extension-table-cell';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import { Node, Extension, mergeAttributes } from '@tiptap/core';
import Suggestion from '@tiptap/suggestion';
import { PluginKey } from '@tiptap/pm/state';
import tippy, { Instance as TippyInstance } from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import {
  Bold, Italic, Underline as UnderlineIcon, Heading, Pilcrow,
  AlignLeft, AlignCenter, AlignRight, List, ListOrdered, RotateCcw,
  Plus, X, Loader, AlertCircle, Table as TableIcon, Palette, Eye, Code
} from 'lucide-react';
import {
  TemplateVariable,
  getTemplateVariables,
  createTemplateVariable
} from '../../lib/variables/service';
import { sanitizeAndInlineEmailHtml } from '../../lib/variables/emailHtmlUtils';
import Modal from '../shared/Modal';
import { MergeToken } from './MergeToken';

/* ── Transform helpers ── */
function preprocessHtmlForTiptap(html: string): string {
  if (!html) return '';
  // Strip <style> tags to avoid CSS leakage
  let clean = html.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
  // Replace {{varName}} with <span data-merge-token="varName">{{varName}}</span>
  clean = clean.replace(/\{\{([a-zA-Z0-9._-]+)\}\}/g, (_match, name) => {
    return `<span data-merge-token="${name}">{{${name}}}</span>`;
  });
  return clean;
}

function postprocessHtmlFromTiptap(html: string): string {
  if (!html) return '';
  // Convert <span data-merge-token="varName">...</span> back to {{varName}}
  return html.replace(/<span[^>]*data-merge-token="([^"]+)"[^>]*>[\s\S]*?<\/span>/gi, '{{$1}}');
}

/* ── Toolbar Button ── */
function ToolbarBtn({
  onClick,
  active,
  children,
  title,
}: {
  onClick: () => void;
  active?: boolean;
  children: React.ReactNode;
  title?: string;
}) {
  return (
    <button
      type="button"
      onMouseDown={(e) => {
        e.preventDefault();
        onClick();
      }}
      className={`p-1.5 rounded-md transition-all cursor-pointer text-xs ${
        active ? 'bg-blue-100 text-brand font-bold' : 'text-slate-600 hover:bg-slate-100'
      }`}
      title={title}
    >
      {children}
    </button>
  );
}

/* ── Suggestion List Popup Renderer ── */
class SuggestionListWrapper {
  component: ReactRenderer<any> | null = null;
  popup: TippyInstance[] | null = null;

  onStart(props: any) {
    this.component = new ReactRenderer(SuggestionListRenderer, {
      props,
      editor: props.editor,
    });

    if (!props.clientRect) return;

    this.popup = tippy('body', {
      getReferenceClientRect: props.clientRect,
      appendTo: () => document.body,
      content: this.component.element,
      showOnCreate: true,
      interactive: true,
      trigger: 'manual',
      placement: 'bottom-start',
    });
  }

  onUpdate(props: any) {
    this.component?.updateProps(props);
    if (!props.clientRect) return;
    this.popup?.[0]?.setProps({
      getReferenceClientRect: props.clientRect,
    });
  }

  onKeyDown(props: any) {
    if (props.event.key === 'Escape') {
      this.popup?.[0]?.hide();
      return true;
    }
    return (this.component?.ref as any)?.onKeyDown(props) || false;
  }

  onExit() {
    this.popup?.[0]?.destroy();
    this.component?.destroy();
  }
}

function SuggestionListRenderer(props: any) {
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    setSelectedIndex(0);
  }, [props.items]);

  const selectItem = (index: number) => {
    const item = props.items[index];
    if (item) {
      props.command(item);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : props.items.length - 1));
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev < props.items.length - 1 ? prev + 1 : 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        selectItem(selectedIndex);
      }
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [selectedIndex, props.items]);

  if (!props.items || props.items.length === 0) {
    return null;
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden min-w-[220px] max-h-64 overflow-y-auto p-1 text-xs z-[9999]">
      {props.items.map((item: any, index: number) => (
        <button
          key={item.name + index}
          type="button"
          onClick={() => selectItem(index)}
          className={`w-full text-left px-3 py-2 rounded-lg flex items-center justify-between transition-colors cursor-pointer ${
            index === selectedIndex ? 'bg-purple-50 text-purple-900 font-semibold' : 'text-slate-700 hover:bg-slate-50'
          }`}
        >
          {item.isCreateOption ? (
            <div className="flex items-center gap-2 text-brand font-bold">
              <Plus className="h-3.5 w-3.5" />
              <span>Create variable "{item.name}"</span>
            </div>
          ) : (
            <>
              <div className="min-w-0 flex-1">
                <div className="font-mono text-purple-700 font-bold">{`{{${item.name}}}`}</div>
                <div className="text-[11px] text-slate-500 truncate">{item.label}</div>
              </div>
              <span className="text-[11px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded ml-2 shrink-0">
                {item.group}
              </span>
            </>
          )}
        </button>
      ))}
    </div>
  );
}

/* ── Main TiptapEditor Component ── */
interface TiptapEditorProps {
  html: string;
  onChange: (html: string) => void;
  disabled?: boolean;
  adminEmail: string;
  onVariableCreated?: () => void;
}

export function TiptapEditor({
  html,
  onChange,
  disabled = false,
  adminEmail,
  onVariableCreated,
}: TiptapEditorProps) {
  const [variables, setVariables] = useState<TemplateVariable[]>([]);
  const [viewMode, setViewMode] = useState<'visual' | 'code'>('visual');

  // Inline "Create Variable" Modal State
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newVarName, setNewVarName] = useState('');
  const [newVarLabel, setNewVarLabel] = useState('');
  const [newVarGroup, setNewVarGroup] = useState('Applicant');
  const [newVarDataPath, setNewVarDataPath] = useState('');
  const [newVarSampleValue, setNewVarSampleValue] = useState('');
  const [createSaving, setCreateSaving] = useState(false);
  const [createError, setCreateError] = useState('');
  const pendingRangeRef = useRef<any>(null);

  const loadVars = useCallback(async () => {
    try {
      const list = await getTemplateVariables(false);
      setVariables(list);
    } catch (err) {
      console.error('Failed to load variables in TiptapEditor:', err);
    }
  }, []);

  useEffect(() => {
    loadVars();
  }, [loadVars]);

  const variablesRef = useRef(variables);
  useEffect(() => {
    variablesRef.current = variables;
  }, [variables]);

  // Helper for matching and creating token suggestion lists
  const getFilteredTokens = useCallback((query: string) => {
    const all = variablesRef.current;
    const q = query.toLowerCase().trim();

    // Check if query contains modifier split like "first_name | uppercase" or "first_name:upper"
    let modifier = '';
    let baseQuery = q;
    if (q.includes('|') || q.includes(':')) {
      const parts = q.split(/[:|]/);
      baseQuery = parts[0].trim();
      modifier = parts[1]?.trim() || '';
    }

    const filtered: Array<{ name: string; label: string; group: string; modifier?: string; isCreateOption?: boolean }> = all
      .filter(
        (v) =>
          v.name.toLowerCase().includes(baseQuery) ||
          v.label.toLowerCase().includes(baseQuery) ||
          v.group.toLowerCase().includes(baseQuery)
      )
      .map((v) => ({
        name: modifier ? `${v.name} | ${modifier}` : v.name,
        label: v.label,
        group: v.group,
        modifier,
      }));

    if (baseQuery && !all.some((v) => v.name.toLowerCase() === baseQuery)) {
      filtered.push({
        name: baseQuery,
        label: `Create variable "${baseQuery}"`,
        group: 'New Variable',
        isCreateOption: true,
      });
    }

    return filtered.slice(0, 12);
  }, []);

  // Create suggestion extension dynamically for both @ and {{
  const TokenSuggestionExtension = useMemo(() => {
    return Extension.create({
      name: 'tokenSuggestionExtension',
      addProseMirrorPlugins() {
        const createPlugin = (key: string, char: string) =>
          Suggestion({
            pluginKey: new PluginKey(key),
            editor: this.editor,
            char,
            items: ({ query }: { query: string }) => getFilteredTokens(query),
            render: () => new SuggestionListWrapper() as any,
            command: ({ editor, range, props }: any) => {
              if (props.isCreateOption) {
                pendingRangeRef.current = range;
                setNewVarName(props.name);
                setNewVarLabel(props.name);
                setNewVarGroup('Applicant');
                setNewVarDataPath('');
                setNewVarSampleValue('');
                setCreateError('');
                setShowCreateModal(true);
              } else {
                editor
                  .chain()
                  .focus()
                  .deleteRange(range)
                  .insertContent({
                    type: 'mergeToken',
                    attrs: { name: props.name },
                  })
                  .run();
              }
            },
          });

        return [
          createPlugin('mergeTokenSuggestionAt', '@'),
          createPlugin('mergeTokenSuggestionBrackets', '{{'),
        ];
      },
    });
  }, [getFilteredTokens]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
        underline: false,
      }),
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph', 'tableCell', 'tableHeader'],
      }),
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
      TextStyle,
      Color,
      MergeToken,
      TokenSuggestionExtension,
    ],
    content: preprocessHtmlForTiptap(html),
    editable: !disabled,
    onUpdate: ({ editor }) => {
      const rawHtml = editor.getHTML();
      const cleanHtml = postprocessHtmlFromTiptap(rawHtml);
      onChange(cleanHtml);
    },
  });

  // Sync content if incoming html changes externally
  useEffect(() => {
    if (!editor) return;
    const currentPost = postprocessHtmlFromTiptap(editor.getHTML());
    if (currentPost !== html) {
      editor.commands.setContent(preprocessHtmlForTiptap(html));
    }
  }, [html, editor]);

  const handleCreateVariableSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateError('');

    const cleanName = newVarName.trim();
    const cleanLabel = newVarLabel.trim();
    if (!cleanName) {
      setCreateError('Variable name is required');
      return;
    }
    if (!/^[a-zA-Z0-9._-]+$/.test(cleanName)) {
      setCreateError('Name can only contain letters, numbers, dots, hyphens, and underscores');
      return;
    }
    if (!cleanLabel) {
      setCreateError('Label is required');
      return;
    }

    setCreateSaving(true);
    try {
      await createTemplateVariable({
        name: cleanName,
        label: cleanLabel,
        group: newVarGroup,
        dataPath: newVarDataPath.trim() || null,
        sampleValue: newVarSampleValue.trim(),
        deprecated: false,
      });

      await loadVars();
      if (onVariableCreated) onVariableCreated();

      // Insert token into editor at pending range
      if (editor && pendingRangeRef.current) {
        editor
          .chain()
          .focus()
          .deleteRange(pendingRangeRef.current)
          .insertContent({
            type: 'mergeToken',
            attrs: { name: cleanName },
          })
          .run();
      }

      setShowCreateModal(false);
    } catch (err: any) {
      console.error('Failed to create variable mid-edit:', err);
      setCreateError(err.message || 'Failed to create variable');
    } finally {
      setCreateSaving(false);
    }
  };

  if (!editor) return null;

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 px-3 py-1.5 border-b border-slate-200 bg-white shrink-0 flex-wrap">
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive('bold')}
          title="Bold (Ctrl+B)"
        >
          <Bold className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive('italic')}
          title="Italic (Ctrl+I)"
        >
          <Italic className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          active={editor.isActive('underline')}
          title="Underline (Ctrl+U)"
        >
          <UnderlineIcon className="h-3.5 w-3.5" />
        </ToolbarBtn>

        <div className="w-px h-4 bg-slate-200 mx-1" />

        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          active={editor.isActive('heading', { level: 2 })}
          title="Heading 2"
        >
          <Heading className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().setParagraph().run()}
          active={editor.isActive('paragraph')}
          title="Paragraph"
        >
          <Pilcrow className="h-3.5 w-3.5" />
        </ToolbarBtn>

        <div className="w-px h-4 bg-slate-200 mx-1" />

        <ToolbarBtn
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          active={editor.isActive({ textAlign: 'left' })}
          title="Align Left"
        >
          <AlignLeft className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          active={editor.isActive({ textAlign: 'center' })}
          title="Align Center"
        >
          <AlignCenter className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          active={editor.isActive({ textAlign: 'right' })}
          title="Align Right"
        >
          <AlignRight className="h-3.5 w-3.5" />
        </ToolbarBtn>

        <div className="w-px h-4 bg-slate-200 mx-1" />

        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive('bulletList')}
          title="Bullet List"
        >
          <List className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive('orderedList')}
          title="Numbered List"
        >
          <ListOrdered className="h-3.5 w-3.5" />
        </ToolbarBtn>

        <div className="w-px h-4 bg-slate-200 mx-1" />

        <ToolbarBtn
          onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
          active={editor.isActive('table')}
          title="Insert Table"
        >
          <TableIcon className="h-3.5 w-3.5" />
        </ToolbarBtn>

        <div className="flex items-center gap-1 px-1" title="Text Color">
          <Palette className="h-3.5 w-3.5 text-slate-400" />
          <input
            type="color"
            value={editor.getAttributes('textStyle').color || '#000000'}
            onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
            className="w-5 h-5 cursor-pointer rounded border border-slate-200 p-0 bg-transparent"
          />
        </div>

        <div className="w-px h-4 bg-slate-200 mx-1" />

        <ToolbarBtn
          onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().unsetColor().run()}
          title="Clear Formatting"
        >
          <RotateCcw className="h-3.5 w-3.5" />
        </ToolbarBtn>

        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            onClick={() => {
              if (viewMode === 'code') {
                // Switching from code to visual
                editor.commands.setContent(preprocessHtmlForTiptap(html));
                setViewMode('visual');
              } else {
                setViewMode('code');
              }
            }}
            className={`px-2.5 py-1 text-xs font-bold rounded-lg flex items-center gap-1 transition-all cursor-pointer ${
              viewMode === 'visual'
                ? 'bg-blue-50 text-blue-700 border border-blue-200'
                : 'bg-slate-100 text-slate-700 border border-slate-200'
            }`}
          >
            {viewMode === 'visual' ? (
              <><Code className="h-3.5 w-3.5" /><span>HTML Source</span></>
            ) : (
              <><Eye className="h-3.5 w-3.5" /><span>Visual Editor</span></>
            )}
          </button>
        </div>
      </div>

      {/* Editor Content Area */}
      <div className="flex-1 overflow-auto bg-slate-100/70 p-8 flex justify-center">
        {viewMode === 'visual' ? (
          <div className="w-full max-w-[210mm] min-h-[297mm] bg-white shadow-xl border border-slate-200/80 rounded-sm p-12 md:p-16 text-slate-800 leading-relaxed font-serif text-[12pt]">
            <EditorContent editor={editor} className="outline-none min-h-[250mm]" />
          </div>
        ) : (
          <div className="w-full max-w-[210mm] min-h-[297mm] bg-slate-50 text-slate-800 border border-slate-200 p-6 rounded-xl shadow-xl font-mono text-xs overflow-auto">
            <textarea
              value={html}
              onChange={(e) => {
                const clean = sanitizeAndInlineEmailHtml(e.target.value);
                onChange(clean);
              }}
              disabled={disabled}
              className="w-full h-full min-h-[250mm] bg-transparent text-slate-800 border-0 outline-none resize-none font-mono text-xs leading-relaxed"
              placeholder="<!-- Write or paste raw HTML here -->"
            />
          </div>
        )}
      </div>

      {/* Inline Create Variable Modal */}
      {showCreateModal && (
        <Modal onClose={() => setShowCreateModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100 mb-4">
              <h3 className="text-sm font-extrabold text-slate-900">Define New Variable</h3>
              <button aria-label="Close" title="Close" onClick={() => setShowCreateModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="h-4 w-4" />
              </button>
            </div>

            <form onSubmit={handleCreateVariableSubmit} className="space-y-3">
              {createError && (
                <div className="p-2.5 bg-rose-50 border border-rose-100 rounded-xl text-rose-600 text-xs flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  {createError}
                </div>
              )}

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Token Name</label>
                <input
                  type="text"
                  value={newVarName}
                  onChange={(e) => setNewVarName(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-mono text-slate-900"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Human Label</label>
                <input
                  type="text"
                  value={newVarLabel}
                  onChange={(e) => setNewVarLabel(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Group</label>
                <select
                  value={newVarGroup}
                  onChange={(e) => setNewVarGroup(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900"
                >
                  <option value="Applicant">Applicant</option>
                  <option value="Address">Address</option>
                  <option value="Passport & Visa">Passport & Visa</option>
                  <option value="Program & Placement">Program & Placement</option>
                  <option value="Education">Education</option>
                  <option value="Employment">Employment</option>
                  <option value="Emergency Contacts">Emergency Contacts</option>
                  <option value="System & Dates">System & Dates</option>
                  <option value="Custom">Custom</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Firestore Data Path (optional)</label>
                <input
                  type="text"
                  placeholder="e.g. personalDetails.firstName"
                  value={newVarDataPath}
                  onChange={(e) => setNewVarDataPath(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-mono text-slate-900"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Sample Value</label>
                <input
                  type="text"
                  placeholder="e.g. John"
                  value={newVarSampleValue}
                  onChange={(e) => setNewVarSampleValue(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900"
                />
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createSaving}
                  className="flex-1 px-4 py-2 btn-primary text-white text-xs font-bold rounded-xl transition-all flex items-center justify-center gap-1.5"
                >
                  {createSaving ? <Loader className="h-3.5 w-3.5 animate-spin" /> : null}
                  Create & Insert Token
                </button>
              </div>
            </form>
          </div>
        </Modal>
      )}
    </div>
  );
}
